import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  createCommandAsyncThunk,
  createQueryAsyncThunk,
} from "../../app/createApiAsyncThunk";
import { RootState } from "../../app/store";
import { EventPayload } from "../../common/types/Event";
import { ORDER_CANCELLED_ALL_SHIFTS_CANCELLED } from "./constants";
import {
  CancelOrderRowBundle,
  cancelOrderRowBundle,
  cancelShiftApi,
  CancelShiftCmd,
  Certificate,
  certificateDefinitionsAutoCompleteQuery,
  CertificatesFilter,
  CreateWorkAgreementDocument,
  doGetWorkAgreementDocumentDownloadUrls,
  doGetWorkAgreementDocumentInfo,
  doGetWorkAgreementDocumentUploadUrl,
  doSetWorkAgreementDocumentSigningDate,
  doUploadWorkAgreementDocument,
  editLongOrderRow,
  editShortOrderRow,
  getCanCancelEmployment,
  getOrderFulfilled,
  getTalentsForLongOrder,
  getWorkAgreement,
  GetWorkAgreementDocumentUploadUrl,
  getWorkAgreementInfoFromOrderRowBundleInfo,
  listOrderRowsByBundleId,
  LongOrderTalents,
  OfferWorkAgreement,
  OrderEdit,
  OrderFilters,
  OrderRowBundleId,
  OrderRowEvent,
  postCancelWorkAgreementOffer,
  postCreateWorkAgreementDocument,
  postOfferWorkAgreement,
  postUpdateWorkAgreement,
  removeShiftsFromBundle,
  searchTalentsByWorkLocationId,
  SendDissassembleBundle,
  SetWorkAgreementDocumentSigningDate,
  shiftCancelledApi,
  ShiftCancelledCmd,
  Skill,
  skillDefinitionsAutoCompleteQuery,
  SkillsFilter,
  TalentWorkOfferStatus,
  UpdateLongOrderRowBundle,
  UpdateOrderHandlers,
  updateOrderHandlers,
  UpdateOrderRowBundle,
  UpdateWorkAgreement,
  WorkAgreement,
  WorkAgreementDocumentDownloadUrls,
  WorkAgreementDocumentInfo,
  WorkAgreementDocumentUploadUrlResponse,
  WorkAgreementInfo,
  WorkLocationSuggestedTalentFilters,
} from "./orderAPI";
import { FulfillmentStatusResponse } from "./orderTypes";
import {
  isOrderRowBundleCancelledEvent,
  isOrderRowBundleTaskSpecChangedEvent,
  isOrderRowEditReasonChangedEvent,
  isWorkOffersTalentAmountChangedEvent,
} from "./types/events";
import { TargetingRuleValues } from "../talentProfile/Employment/EmploymentAdditionalInfo/targetedShifts/TargetedShifts";

export type SliceState = {
  ids: [];
  // TODO: CHANGE THE ENTITIES TYPE TO AVOID USING @ts-ignores
  entities: {};
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

const orderRowsDetailedAdapter = createEntityAdapter<
  OrderEdit,
  OrderEdit["bundleId"]
>({
  sortComparer: (a, b) =>
    new Date(a.dateOrdered)
      .toLocaleDateString()
      .localeCompare(new Date(b.dateOrdered).toLocaleDateString()),
  selectId: (order) => order.bundleId,
});

export const fetchOrderRowsByBundleId = createAsyncThunk<
  OrderEdit,
  OrderFilters,
  { state: RootState }
>(
  "orderRowsDetailed/fetchOrderRowsByBundleId",
  async (filters: OrderFilters, { requestId }) => {
    const res = await listOrderRowsByBundleId(filters, { requestId });
    return res.data;
  },
);

export const cancelRowBundle = createAsyncThunk<
  Promise<any>,
  CancelOrderRowBundle,
  { state: RootState }
>(
  "orderRowsDetailed/cancelOrderRowBundle",
  async (data: CancelOrderRowBundle) => {
    const res = await cancelOrderRowBundle(data);
    return res;
  },
);

export const cancelShortOrderShift = createAsyncThunk<
  OrderRowEvent[],
  CancelShiftCmd & { rowId: string; bundleId: string },
  { state: RootState }
>(
  "orderRowsDetailed/cancelShortOrderShift",
  async (data: CancelShiftCmd & { rowId: string; bundleId: string }) => {
    const res = await cancelShiftApi(data);
    return res;
  },
);

export const shiftCancelled = createAsyncThunk<
  EventPayload<unknown>[],
  ShiftCancelledCmd & { editor?: string },
  { state: RootState }
>(
  "orderRowsDetailed/shiftCancelled",
  async ({ editor, ...payload }, { requestId }) => {
    const res = await shiftCancelledApi(payload, { requestId });
    return res;
  },
);

export const findTalentsToWorkLocation = createAsyncThunk<
  TalentWorkOfferStatus[],
  WorkLocationSuggestedTalentFilters,
  { state: RootState }
>(
  "orderRowsDetailed/findTalentsToWorkLocation",
  async (filters: WorkLocationSuggestedTalentFilters) => {
    const res = await searchTalentsByWorkLocationId(
      filters.workLocationId,
      filters.bundleId,
    );
    return res.data;
  },
);

export const saveOrderRow = createAsyncThunk<
  OrderRowEvent[],
  UpdateOrderRowBundle,
  { state: RootState }
>(
  "orderRowsDetailed/editOrderRowBundle",
  async (data: UpdateOrderRowBundle) => {
    const res = await editShortOrderRow(data);
    return res;
  },
);

export const saveLongOrderRow = createAsyncThunk<
  OrderRowEvent[],
  UpdateLongOrderRowBundle,
  { state: RootState }
>(
  "orderRowsDetailed/editLongOrderRowBundle",
  async (data: UpdateLongOrderRowBundle) => {
    const res = await editLongOrderRow(data);
    return res;
  },
);

export const fetchCertificateDefinitions = createAsyncThunk<
  Certificate[],
  CertificatesFilter,
  { state: RootState }
>(
  "orderRowsDetailed/fetchCertificateDefinitions",
  async (filters: CertificatesFilter) => {
    const res = await certificateDefinitionsAutoCompleteQuery(filters);
    return res.data;
  },
);

export const fetchSkillDefinitions = createAsyncThunk<
  Skill[],
  SkillsFilter,
  { state: RootState }
>("orderRowsDetailed/fetchSkillDefinitions", async (filters: SkillsFilter) => {
  const res = await skillDefinitionsAutoCompleteQuery(filters);
  return res.data;
});

export const disassembleOrderRowBundle = createAsyncThunk<
  OrderRowEvent[],
  SendDissassembleBundle,
  { state: RootState }
>(
  "orderRowsDetailed/disassembleOrderRowBundle",
  async (params: SendDissassembleBundle) => {
    const res = await removeShiftsFromBundle(params);
    return res.data;
  },
);

export const changeOrderHandlers = createAsyncThunk<
  OrderRowEvent[],
  UpdateOrderHandlers,
  { state: RootState }
>(
  "orderRowsDetailed/changeOrderHandlers",
  async (params: UpdateOrderHandlers) => {
    const res = await updateOrderHandlers(params);
    return res.data;
  },
);

export const fetchLongOrderTalents = createAsyncThunk<
  LongOrderTalents,
  string,
  { state: RootState }
>("orderRowsDetailed/fetchLongOrderTalents", async (bundleId: string) => {
  const res = await getTalentsForLongOrder(bundleId);
  return res.data;
});

export const offerWorkAgreement = createAsyncThunk<
  OrderRowEvent[],
  OfferWorkAgreement,
  { state: RootState }
>(
  "orderRowsDetailed/offerWorkAgreement",
  async (params: OfferWorkAgreement) => {
    const res = await postOfferWorkAgreement(params);
    return res.data;
  },
);

export const updateWorkAgreement = createAsyncThunk<
  OrderRowEvent[],
  UpdateWorkAgreement,
  { state: RootState }
>(
  "orderRowsDetailed/updateWorkAgreement",
  async (params: UpdateWorkAgreement) => {
    const res = await postUpdateWorkAgreement(params);
    return res.data;
  },
);

export const fetchWorkAgreementInfo = createAsyncThunk<
  WorkAgreementInfo,
  OrderRowBundleId,
  { state: RootState }
>(
  "orderRowsDetailed/fetchWorkAgreementInfo",
  async (bundleId: OrderRowBundleId) => {
    const res = await getWorkAgreementInfoFromOrderRowBundleInfo(bundleId);
    return res.data;
  },
);

export const fetchWorkAgreement = createAsyncThunk<
  WorkAgreement,
  string,
  { state: RootState }
>("orderRowsDetailed/fetchWorkAgreement", async (id: string) => {
  const res = await getWorkAgreement(id);
  return res.data;
});

export const cancelWorkAgreementOffer = createAsyncThunk<
  OrderRowEvent[],
  { workAgreementId: string; version: number },
  { state: RootState }
>(
  "orderRowsDetailed/sendCancelWorkAgreementOffer",
  async ({ workAgreementId, version }) => {
    const res = await postCancelWorkAgreementOffer(workAgreementId, version);
    return res.data;
  },
);

export const fetchOrderFulfilledStatus = createQueryAsyncThunk<
  FulfillmentStatusResponse,
  { bundleId: string }
>("orderRowsDetailed/fetchOrderFulfilledStatus", getOrderFulfilled);

export const canCancelEmployment = createQueryAsyncThunk<
  boolean,
  { bundleId: string; talentId: string }
>("orderRowsDetailed/canCancelEmployment", getCanCancelEmployment);

export const createWorkAgreementDocument = createCommandAsyncThunk<
  CreateWorkAgreementDocument,
  any
>(
  "orderRowsDetailed/createWorkAgreementDocument",
  postCreateWorkAgreementDocument,
);

export const getWorkAgreementDocumentUploadUrl = createQueryAsyncThunk<
  WorkAgreementDocumentUploadUrlResponse,
  GetWorkAgreementDocumentUploadUrl
>(
  "orderRowsDetailed/getWorkAgreementDocumentUploadUrl",
  doGetWorkAgreementDocumentUploadUrl,
);

export const uploadWorkAgreementDocument = createAsyncThunk<
  void,
  { url: string; file: File },
  { state: RootState }
>(
  "orderRowsDetailed/uploadWorkAgreementDocument",
  async ({ url, file }) => await doUploadWorkAgreementDocument(url, file),
);

export const getWorkAgreementDocumentInfo = createQueryAsyncThunk<
  WorkAgreementDocumentInfo,
  { workAgreementDocumentId: string }
>(
  "orderRowsDetailed/getWorkAgreementDocumentInfo",
  doGetWorkAgreementDocumentInfo,
);

export const setWorkAgreementDocumentSigningDate = createCommandAsyncThunk<
  SetWorkAgreementDocumentSigningDate,
  any
>(
  "orderRowsDetailed/setWorkAgreementDocumentSigningDate",
  doSetWorkAgreementDocumentSigningDate,
);

export const getWorkAgreementDocumentDownloadUrls = createQueryAsyncThunk<
  WorkAgreementDocumentDownloadUrls,
  { workAgreementDocumentId: string }
>(
  "orderRowsDetailed/getWorkAgreementDocumentDownloadUrls",
  doGetWorkAgreementDocumentDownloadUrls,
);

export const orderRowsDetailedSlice = createSlice({
  name: "orderRowsDetailed",
  initialState: orderRowsDetailedAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {
    updateTargetedShiftTargetingRules: (
      state,
      action: PayloadAction<{ bundleId: string; rules: TargetingRuleValues[] }>,
    ) => {
      const { bundleId, rules } = action.payload;
      //@ts-ignore
      const orderBundle = state.entities[bundleId] as OrderEdit;
      if (orderBundle) {
        const handledRules = rules.map((rule) => ({
          custCompany: {
            id: rule.customerCompany?.id ?? "",
            name: rule.customerCompany?.name ?? "",
          },
          custOrgs: rule.customerOrganization
            ? [rule.customerOrganization]
            : [],
          workLocs: rule.workLocations ?? [],
          workTitles: rule.workTitles?.map((wt) => wt.id) ?? [],
        }));
        orderBundle.targetedShiftTargetingRules = handledRules;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderRowsByBundleId.fulfilled, (state, action) => {
        const value = {
          ...action.payload,
          id: action.payload.bundleId,
          shiftDetails: action.payload.shiftDetails,
        };
        orderRowsDetailedAdapter.setOne(state, value);
        state.status = "succeeded";
      })
      .addCase(fetchOrderRowsByBundleId.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(fetchOrderRowsByBundleId.pending, (state) => {
        state.status = "pending";
      })
      .addCase(saveOrderRow.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.length !== 0) {
          const { bundleId } = action.meta.arg;
          //@ts-ignore
          const orderBundle = state.entities[bundleId] as OrderEdit;
          const editReasonPayloads = action.payload.filter(
            (event) => event.name === "ordRowEdtRsnChg",
          );
          const automatchStatus = action.payload.find(
            (event) => event.name === "wrkOffMtchStatusUpd",
          )?.payload;
          // Get highest version of all payloads where id matches
          // and event name starts with aggregateName "ordRow"
          const highestVersion = action.payload
            .filter((event) => event.name.startsWith("ordRow"))
            .reduce((max, event) => Math.max(max, event.version), 0);

          const oldEditReasons = orderBundle.reasonsForEdits ?? [];
          const newEditReasons = [
            ...oldEditReasons,
            ...editReasonPayloads.map((er) => ({
              editReason: er.payload.editReason,
              editor: action.meta.arg.editor ?? "",
              time: er.payload.editTime,
            })),
          ];

          const { payload: exhaustedEvent } = action.payload.find(
            (event) => event.name === "workOffAMExhaustedStateUpd",
          ) || {
            payload: { exhausted: orderBundle.status.isAutomatchingExhausted },
          };
          const newStatus = {
            ...orderBundle.status,
            isAutomatchingExhausted: exhaustedEvent.exhausted,
            ...(automatchStatus &&
              automatchStatus.status && {
                automatchingStatus: automatchStatus.status,
                ...(automatchStatus.delayMinutes && {
                  automatchingStartDelayMinutes: automatchStatus.delayMinutes,
                }),
              }),
          };
          const changes = {
            ...action.meta.arg,
            orderRows: [...action.meta.arg.orderRows],
            reasonsForEdits: newEditReasons,
            status: newStatus,
            version: highestVersion,
          };

          orderRowsDetailedAdapter.updateOne(state, {
            id: action.meta.arg.bundleId,
            changes,
          });
        }
      })
      .addCase(saveOrderRow.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(saveOrderRow.pending, (state) => {
        state.status = "pending";
      })
      .addCase(saveLongOrderRow.fulfilled, (state, action) => {
        //@ts-ignore
        const orderBundle = state.entities[
          action.meta.arg.bundleId
        ] as OrderEdit;
        const editReasonPayload = action.payload.find(
          (event) => event.name === "ordRowEdtRsnChg",
        );
        // Get highest version of all payloads where id matches
        // and event name starts with aggregateName "ordRow"
        const highestVersion = action.payload
          .filter((event) => event.name.startsWith("ordRow"))
          .reduce((max, event) => Math.max(max, event.version), 0);

        let rfe = orderBundle.reasonsForEdits || [];
        if (editReasonPayload) {
          const { payload: orderRowComment } = editReasonPayload;
          rfe = [
            ...rfe,
            {
              ...orderRowComment,
              editor: "",
              time: orderRowComment.editTime,
            },
          ];
        }

        const newStatus = action.payload.find(
          (event) => event.name === "ordRowBndlStatusChg",
        )?.payload.status;

        const changes = {
          reasonsForEdits: rfe,
          version: highestVersion,
          amount: action.meta.arg.requiredTalentAmount,
          reasonForTemporaryEmployment:
            action.meta.arg.reasonForTemporaryEmployment,
          workStarts: new Date(action.meta.arg.startDate).getTime(),
          ...(action.meta.arg.endDate && {
            workEnds: new Date(action.meta.arg.endDate).getTime(),
          }),
          customerCostCenterIds: action.meta.arg.customerCostCenterIds,
          shortWorkTimeDescriptionFi:
            action.meta.arg.shortWorkTimeDescriptionFi,
          shortWorkTimeDescriptionEn:
            action.meta.arg.shortWorkTimeDescriptionEn,
          longWorkTimeDescriptionFi: action.meta.arg.longWorkTimeDescriptionFi,
          longWorkTimeDescriptionEn: action.meta.arg.longWorkTimeDescriptionEn,
          taskSpec: {
            ...orderBundle.taskSpec,
            ...(action.meta.arg.talentRequirements.languages && {
              languageSkills: action.meta.arg.talentRequirements.languages,
            }),
            ...(action.meta.arg.talentRequirements.skills && {
              skillIds: action.meta.arg.talentRequirements.skills,
            }),
            ...(action.meta.arg.talentRequirements.certificates && {
              certificateIds: action.meta.arg.talentRequirements.certificates,
            }),
            ...(action.meta.arg.talentRequirements.trainings && {
              trainings: action.meta.arg.talentRequirements.trainings,
            }),
            description: action.meta.arg.workDescriptionFi,
            descriptionEn: action.meta.arg.workDescriptionEn,
            descriptionForTalent: action.meta.arg.arrivalInstructionsFi,
            descriptionForTalentEn: action.meta.arg.arrivalInstructionsEn,
            tesVariation: action.meta.arg.tesVariation,
          },
          descriptionToEezyAndCustomer:
            action.meta.arg.descriptionToEezyAndCustomer,
          status: {
            ...orderBundle.status,
            ...(newStatus && { mainStatus: newStatus }),
          },
        } as OrderEdit;

        orderRowsDetailedAdapter.updateOne(state, {
          ...action.meta.arg,
          id: action.meta.arg.bundleId,
          changes,
        });

        state.status = "succeeded";
      })
      .addCase(saveLongOrderRow.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(saveLongOrderRow.pending, (state) => {
        state.status = "pending";
      })
      .addCase(cancelShortOrderShift.fulfilled, (state, action) => {
        state.status = "succeeded";
        // use passed arguments as events are not returned
        const { assignmentId, bundleId } = action.meta.arg;
        if (action.payload.length !== 0) {
          const { payload: assignEvent } = action.payload.find(
            (event) => event.name === "assgnCancel",
          )!!;
          //@ts-ignore
          const orderBundle = state.entities[bundleId] as OrderEdit;
          const cancelledShift = orderBundle.shiftDetails.find(
            (shift) =>
              shift.assignedTalent &&
              shift.assignedTalent.assignmentId === assignmentId, //use assignment id
          )!!;
          if (cancelledShift.assignedTalent) {
            cancelledShift.assignedTalent.reasonForCancellation =
              assignEvent.reason;
            cancelledShift.assignedTalent.talentGetsPaid =
              assignEvent.talentGetsPaid;
          }
          // cancelReason is set/cleared in shiftCancelled.fulfilled based on the
          // ordRowBndlCancl event from the backend, which is the authoritative signal.
          orderRowsDetailedAdapter.updateOne(state, {
            id: action.meta.arg.bundleId,
            changes: orderBundle,
          });
        }
      })
      .addCase(cancelShortOrderShift.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(cancelShortOrderShift.pending, (state) => {
        state.status = "pending";
      })
      .addCase(shiftCancelled.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (!action.payload?.length) {
          return;
        }
        const events: EventPayload<unknown>[] = action.payload;
        const { bundleId, replaceTalent } = action.meta.arg;
        //@ts-ignore
        const orderBundle = state.entities[bundleId] as OrderEdit;
        if (!orderBundle) {
          return;
        }

        const amountEvent = events.find(isWorkOffersTalentAmountChangedEvent);
        const taskSpecEvent = events.find(isOrderRowBundleTaskSpecChangedEvent);
        const editReasonEvent = events.find(isOrderRowEditReasonChangedEvent);
        const bundleCancelledEvent = events.find(
          isOrderRowBundleCancelledEvent,
        );

        const nextAmount = amountEvent
          ? amountEvent.payload.amount
          : orderBundle.amount;
        const nextTaskSpecAmount = taskSpecEvent
          ? taskSpecEvent.payload.taskSpec.amount
          : orderBundle.orderRows?.[0]?.taskSpec?.amount;

        let rfe = orderBundle.reasonsForEdits || [];
        if (editReasonEvent) {
          const { payload: orderRowComment } = editReasonEvent;
          rfe = [
            ...rfe,
            {
              ...orderRowComment,
              editor: action.meta.arg.editor || orderRowComment.editUser || "",
              time: orderRowComment.editTime,
            },
          ];
        }

        const updatedOrderRows = orderBundle.orderRows.map((row) => {
          if (!taskSpecEvent) {
            return row;
          }
          return {
            ...row,
            taskSpec: {
              ...row.taskSpec,
              amount: nextTaskSpecAmount ?? row.taskSpec.amount,
            },
            editReason: editReasonEvent
              ? [
                  ...(row.editReason || []),
                  {
                    editReason: editReasonEvent.payload.editReason,
                    editor:
                      action.meta.arg.editor ||
                      editReasonEvent.payload.editUser ||
                      "",
                    time: editReasonEvent.payload.editTime,
                  },
                ]
              : row.editReason,
          };
        });

        // - replaceTalent=true means a replacement is sought → order is not cancelled, clear any stale value.
        // - ordRowBndlCancl event means the backend explicitly cancelled the bundle → set cancelReason.
        const cancelReasonChanges = replaceTalent
          ? { cancelReason: undefined }
          : bundleCancelledEvent
            ? { cancelReason: ORDER_CANCELLED_ALL_SHIFTS_CANCELLED }
            : {};

        orderRowsDetailedAdapter.updateOne(state, {
          id: bundleId,
          changes: {
            ...orderBundle,
            amount: nextAmount,
            originalAmount: orderBundle.originalAmount ?? orderBundle.amount,
            orderRows: updatedOrderRows,
            reasonsForEdits: rfe,
            ...cancelReasonChanges,
          },
        });
      })
      .addCase(shiftCancelled.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(shiftCancelled.pending, (state) => {
        state.status = "pending";
      })
      .addCase(changeOrderHandlers.fulfilled, (state, action) => {
        const { bundleId, handlerIds } = action.meta.arg;
        //@ts-ignore
        const orderBundle = state.entities[bundleId] as OrderEdit;
        const handlers = handlerIds.map((id, i) => {
          const handlerInfo = orderBundle.orderHandlers.find(
            (h) => h.id === id,
          );
          return (
            handlerInfo || { id, name: action.meta.arg.handlerNames[i] || "" }
          );
        });
        // Get highest version of all payloads where id matches
        // and event name starts with aggregateName "ordRow"
        const highestVersion = action.payload
          .filter((event) => event.name.startsWith("ordRow"))
          .reduce((max, event) => Math.max(max, event.version), 0);
        orderRowsDetailedAdapter.updateOne(state, {
          id: bundleId,
          changes: {
            ...orderBundle,
            orderHandlers: handlers,
            version: highestVersion,
          },
        });
        state.status = "succeeded";
      })
      .addCase(changeOrderHandlers.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(changeOrderHandlers.pending, (state) => {
        state.status = "pending";
      })
      .addCase(cancelRowBundle.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(cancelRowBundle.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(cancelRowBundle.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchLongOrderTalents.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(fetchLongOrderTalents.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(fetchLongOrderTalents.pending, (state) => {
        state.status = "pending";
      })
      .addCase(updateWorkAgreement.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(updateWorkAgreement.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(updateWorkAgreement.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchWorkAgreement.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(fetchWorkAgreement.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(fetchWorkAgreement.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchWorkAgreementInfo.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(fetchWorkAgreementInfo.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(fetchWorkAgreementInfo.pending, (state) => {
        state.status = "pending";
      })
      .addCase(cancelWorkAgreementOffer.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(cancelWorkAgreementOffer.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(cancelWorkAgreementOffer.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchOrderFulfilledStatus.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(fetchOrderFulfilledStatus.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(fetchOrderFulfilledStatus.pending, (state) => {
        state.status = "pending";
      })
      .addCase(canCancelEmployment.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(canCancelEmployment.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(canCancelEmployment.pending, (state) => {
        state.status = "pending";
      });
  },
});

export const { selectById: selectOrderRowById } =
  orderRowsDetailedAdapter.getSelectors(
    (state: RootState) => state.orderRowsDetailed,
  );

export const selectOrderRowsStatus = (state: RootState) =>
  state.orderRowsDetailed.status;

export const selectOrderTitleByBundleId = createSelector(
  [
    selectOrderRowsStatus,
    (state: RootState, id?: string) => id,
    (state: RootState, id?: string) =>
      id ? selectOrderRowById(state, id) : undefined,
  ],
  (status, id, row) => {
    if (status === "pending") return "";
    if (!id) return undefined;
    return row?.title;
  },
);

export const { updateTargetedShiftTargetingRules } =
  orderRowsDetailedSlice.actions;

export const orderRowsDetailedReducer = orderRowsDetailedSlice.reducer;
